import { ButtonBase, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

export interface QuotedReplyStripProps {
  /** "Customer" / "Bot" / "Operator" — already resolved by the caller (design §11.4). */
  who: string;
  /** Preformatted clock reading, e.g. "14:02". */
  time: string;
  /** One-line preview of the quoted body (its text, an attachment type label, or the masked sentence). */
  preview: string;
  /** `true` when the quoted message is an OPERATOR row — bronze accent instead of teal. */
  isOperatorQuote?: boolean;
  onJump: () => void;
}

/**
 * The reply-threading strip rendered above a bubble's body when `replyToMessageId` resolves to a
 * loaded row (design §11.4). Renders nothing when unresolved — never a "message not loaded" stub —
 * so the caller simply omits this component in that case.
 */
export function QuotedReplyStrip({
  who,
  time,
  preview,
  isOperatorQuote,
  onJump,
}: QuotedReplyStripProps) {
  const { t } = useTranslation('conversations');
  return (
    <ButtonBase
      onClick={onJump}
      aria-label={t('timeline.jumpToQuoted')}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        borderLeft: '3px solid',
        borderColor: isOperatorQuote ? 'secondary.main' : 'primary.main',
        pl: 1,
        py: 0.25,
        mb: 0.5,
        borderRadius: 0.5,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Typography variant="caption" color="text.secondary" component="div">
        {t('timeline.replyingTo', { who, time })}
      </Typography>
      <Typography variant="body2" color="text.secondary" noWrap>
        {preview}
      </Typography>
    </ButtonBase>
  );
}
